/* eslint-disable */
import React, { useEffect, useState } from "react";
import { Row, Col, Container } from "react-bootstrap";
import { useParams } from "react-router-dom";

import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowUpFromBracket, faExclamation, faSpinner } from "@fortawesome/free-solid-svg-icons";
import { faCircle } from "@fortawesome/free-regular-svg-icons";
import { useAppDispatch, useAppSelector } from "../../app/hooks";

import { useHightAndMargin } from "../../features/appPage/AppPage";
import ComparePdfsArea from "./ComparePdfsArea";
import { MatchTable } from "./MatchTable";
import { selectActiveReport, setActiveReportId } from "../../reducer/reportsSlice";
import { selectBlobsSlice } from "../../reducer/blobsSlice";
import * as XLSX from "xlsx";
import { YesNoModal } from "../../features/yesNoModal/yesNoModal";

interface IExportData {
  en_page: number;
  de_page: number;
  en_match: string;
  de_match: string;
  correctness?: boolean;
}

const Report: React.FC = () => {
  const { setAddMargin } = useHightAndMargin();
  useEffect(() => {
    setAddMargin(false);
    return () => setAddMargin(true);
  }, []);

  const { reportId } = useParams();
  const dispatch = useAppDispatch();
  const activeReport = useAppSelector(selectActiveReport);
  const reportName = activeReport?.report_name;
  const blobsSlice = useAppSelector(selectBlobsSlice);
  const numOfMistakes = blobsSlice.status === "success" ? blobsSlice.data.length : 0;
  const progress = activeReport?.pages_processed && activeReport?.total_pages
    ? (Number(100 * activeReport.pages_processed) / Number(activeReport.total_pages)).toFixed(2) : undefined;

  const doc_check = activeReport?.doc_check;
  const [showDocCheckMessage, setShowDocCheckMessage] = useState(false);
  useEffect(() => {
    if (reportId && reportId !== activeReport?.job_id) dispatch(setActiveReportId(reportId));

    const isFinancialReport = doc_check?.en && doc_check?.de;
    const localStorageKey = `hasShownWarningBefore_${reportId}`;
    const hasShownWarningBefore = localStorage.getItem(localStorageKey);

    // isFinancialReport has to be false NOT undefined
    // because undefined means that the doc_check is not processed yet by the backend
    if (activeReport?.isOwned && isFinancialReport === false && !hasShownWarningBefore) {
      setShowDocCheckMessage(true);
      // this if block is needed while using React.StrictMode
      if (process.env.NODE_ENV === "development") {
        if (hasShownWarningBefore === "")
          localStorage.setItem(localStorageKey, "true");
        else
          localStorage.setItem(localStorageKey, "");
        return;
      }
      localStorage.setItem(localStorageKey, "true");
    }
  }, [activeReport, dispatch, reportId, doc_check]);

  const [t] = useTranslation("report");
  // // indicates whether the user has dismissed the warning of the document not looking
  // // like a CSR report.
  // const [dismissedDocError, setDismissedDocError] = useState<boolean>(false);
  // const [highlightedBlobIds, setHighlightedBlobIds] = useState<Set<number>>(new Set());
  // const [startPagePDF, setStartPagePDF] = useState<number>(1);

  // /**
  //  * Updates the highlighted blobs by the pdf viewer as well as the initial page
  //  * @param blobIds the ids of blobs to highlight
  //  * @param page the page to show first
  //  */
  // const jumpToBlob = (blobIds: Set<number>, page: number): void => {
  //   setHighlightedBlobIds(blobIds);
  //   setStartPagePDF(page);
  // };

  // // controls whether loading screen is shown. Always shown when the page loads
  // // set to false through the report loader
  // const [showLoadingScreen, setShowLoadingScreen] = useState<boolean>(true);

  // // wait until all all resources were loaded from backend
  // const requiredResources = [report, pdf, document, checklist, prediction];
  // // checks the how many have been loaded
  // const percentLoaded = (100 * requiredResources.filter((requirement) => requirement !== null).length)
  //   / requiredResources.length;

  // if (showLoadingScreen) {
  //   return (
  //     <ReportLoader
  //       report={report}
  //       percentLoaded={percentLoaded}
  //       job={job}
  //       onReportReady={() => setShowLoadingScreen(false)}
  //     />
  //   );
  // }

  const handleExport = () => {
    console.log("Exporting,,,")
    if (blobsSlice.status !== "success") return;
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(
      blobsSlice.data.map((blob) => ({
        // NOTE: from testing, the order of the columns in the sheet is determined by the order of the keys in the object here
        correctness: blob.correctness,
        en_page: blob.en.page,
        en_match: blob.en.content,
        de_match: blob.de.content,
        de_page: blob.de.page,
      }) satisfies IExportData));
    XLSX.utils.book_append_sheet(wb, ws, "Unstimmigkeiten");
    XLSX.writeFile(wb, `${reportName} - Unstimmigkeiten.xlsx`);
  };

  return (
    <>
      {showDocCheckMessage && <YesNoModal />}
      <Container className="text-white">
        <Row className="row-cols-1 gy-5">
          <Col>
            <h1>{reportName}</h1>
          </Col>
          <Col>
            <p style={{ width: "65%" }}>
              {t("report-area")} {activeReport?.company} {activeReport?.year}
            </p>
          </Col>
          <Col className="my-5">
            <Row className="gx-5">
              <Col xs>
                <span className="fa-layers fa-fw">
                  <FontAwesomeIcon icon={faCircle} size="lg" />
                  <FontAwesomeIcon icon={faExclamation} size="sm" />
                </span>
                <span className="ms-2">
                  Es wurden <b>{numOfMistakes} mögliche Unstimmigkeiten</b> erkannt
                </span>
                <span className="ms-2">
                  {blobsSlice.status === 'loading' && <FontAwesomeIcon icon={faSpinner} size="lg" spin />}
                </span>
              </Col>
              {progress && (
                <Col xs="auto">
                  <span className="rounded-circle border p-2">{progress}%</span>
                  <span className="ms-2">{t("report-analysis")}</span>
                </Col>
              )}
              <Col xs="auto" style={{ cursor: "pointer" }} onClick={handleExport}>
                <FontAwesomeIcon icon={faArrowUpFromBracket} size="lg" />
                <span className="ms-2">{t("report-export")}</span>
              </Col>
            </Row>
            {blobsSlice.status === "loading" && <Row className="gx-5">
              <Col xs>
                Bitte warten Sie, während die Unstimmigkeiten bearbeitet werden, aktualisieren Sie die Seite nicht...
              </Col>
            </Row>}
          </Col>
        </Row>
      </Container>
      <MatchTable />
      <ComparePdfsArea />
    </>
  );
};

export default Report;
